import { LitElement, html } from 'lit';
import translate from '../../utils/webcomponents/translate';

import * as portfolioUtils from './portfolio-utils';
import { formatNumber, formatPercent } from '../../utils/intl';

const DEFAULT_VALUE = '―';

const ribbonItem = ({ title, value, subscript, sentimentClass = '' } = {}) => html`
	<div class="ribbon__item">
		<h3 class="ribbon__title">${title}</h3>

		<div class="quote quote--large">
			<span class="quote__value ${sentimentClass}">${value}</span>
		</div>

		${subscript ? html`<span class="ribbon__label">${subscript}</span>` : ''}
	</div>
`;

const getSentimentClass = (value) => {
	if (Number(value) === 0) {
		return '';
	}

	if (value > 0) {
		return 'is-positive';
	}

	return 'is-negative';
};

class PortfolioPerformance extends LitElement {
	static get properties() {
		return {
			items: { type: Array },
			currency: { type: String },
		};
	}

	get itemsWithCurrentValue() {
		return this.items.filter((i) => i.currentValue);
	}

	get investedValue() {
		return portfolioUtils.portfolioInvestedValue(this.items);
	}

	get currentValue() {
		return portfolioUtils.portfolioCurrentValue(this.itemsWithCurrentValue);
	}

	get profitLossMonetary() {
		return portfolioUtils.portfolioProfitLossMonetary(this.investedValue, this.currentValue);
	}

	get profitLoss() {
		return portfolioUtils.portfolioProfitLoss(this.investedValue, this.currentValue);
	}

	render() {
		const sentimentClass = this.itemsWithCurrentValue.length ? getSentimentClass(this.profitLossMonetary) : '';

		const items = [
			{
				title: translate('portfolio', 'currentValue'),
				value: this.itemsWithCurrentValue.length
					? `${formatNumber(this.currentValue)} ${this.currency}`
					: DEFAULT_VALUE,
			},
			{
				title: translate('portfolio', 'profitLoss'),
				value: this.itemsWithCurrentValue.length
					? `${formatNumber(this.profitLossMonetary)} ${this.currency}`
					: DEFAULT_VALUE,
				subscript: translate('portfolio', 'versusPurchasePrice'),
				sentimentClass,
			},
			{
				title: translate('portfolio', 'profitLoss'),
				value: this.itemsWithCurrentValue.length ? `${formatPercent(this.profitLoss)}` : DEFAULT_VALUE,
				subscript: translate('portfolio', 'versusPurchasePrice'),
				sentimentClass,
			},
			{
				title: translate('portfolio', 'myInvestment'),
				value: this.items.length ? `${formatNumber(this.investedValue)} ${this.currency}` : DEFAULT_VALUE,
			},
		];

		return html` <div class="ribbon">${items.map(ribbonItem)}</div> `;
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-portfolio-performance', PortfolioPerformance);

export default PortfolioPerformance;
